import React from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowLeft, ExternalLink } from "lucide-react";

/**
 * Groepen fotocredits — exact volgens /public/media/colofon.md.
 * Structuur: title, credit-tekst met optionele link, licentie (indien vermeld).
 */
const LEMMER_PHOTOS = [
  {
    title: "Bruisend Lemmer: terrassen aan het water met de kerktoren",
    author: "Jan van der Werf",
    note: "gebruikt met toestemming",
  },
  {
    title: "Terrassen aan het water bij het Dok",
    thanks: true,
  },
  {
    title: "Traditionele zeilschepen in de haven",
    thanks: true,
  },
  {
    title: "Het strand van Lemmer aan het IJsselmeer",
    thanks: true,
  },
  {
    title: "Een gracht in het centrum met bloemen en gevels",
    thanks: true,
  },
  {
    title: "Gezellige terrassen in het centrum",
    thanks: true,
  },
  {
    title: "Strand en water bij Lemmer",
    thanks: true,
  },
  {
    title: "Traditionele zeilschepen op het water",
    thanks: true,
  },
  {
    title: "Standbeeld van vissers in het centrum",
    thanks: true,
  },
  {
    title: "Winkelen in het centrum van Lemmer",
    thanks: true,
  },
  {
    title: "Levendige straat in het centrum",
    thanks: true,
  },
];

const COMMONS_PHOTOS = [
  {
    title: "Ir. D.F. Woudagemaal, gezien vanaf het stroomkanaal",
    author: "Ellywa",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source:
      "https://commons.wikimedia.org/wiki/File:Woudagemaal_zijde_stroomkanaal.jpg",
  },
  {
    title: "Ir. D.F. Woudagemaal met schoorsteen in het landschap",
    author: "Jan dijkstra",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Ir_D.F._Woudagemaal.jpg",
  },
  {
    title: "Historische foto: een schip vaart de haven van Lemmer uit",
    author: "Jacob Olie",
    license: "Public domain",
    licenseUrl: null,
    licenseNote: "geen naamsvermelding verplicht",
    source:
      "https://commons.wikimedia.org/wiki/File:Een_vrachtschip_vaart_de_haven_van_Lemmer_uit,_gezien_vanaf_een_schip_van_de_dienst_Amsterdam_-_Lemmer_-_Groningen_Jacob_Olie_(max_res).jpg",
  },
];

const LemmerItem = ({ photo }) => (
  <li className="py-4 first:pt-0 last:pb-0">
    <p className="font-[Poppins] font-semibold text-[#173A5E] text-base mb-1">
      {photo.title}
    </p>
    <p className="text-sm text-[#3E5871] font-[Inter]">
      {photo.author ? (
        <>
          Foto: <span className="text-[#173A5E] font-medium">{photo.author}</span>
          {photo.note && (
            <span className="text-[#5A7594]"> ({photo.note})</span>
          )}
        </>
      ) : (
        <>
          Foto: met dank aan{" "}
          <a
            href="https://www.lemsternijs.nl"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[#2E9CD6] hover:text-[#173A5E]"
          >
            Lemsternijs.nl
            <ExternalLink className="w-3 h-3" />
          </a>
        </>
      )}
    </p>
  </li>
);

const CommonsItem = ({ photo }) => (
  <li className="py-4 first:pt-0 last:pb-0">
    <p className="font-[Poppins] font-semibold text-[#173A5E] text-base mb-1">
      {photo.title}
    </p>
    <p className="text-sm text-[#3E5871] font-[Inter]">
      Foto:{" "}
      <span className="text-[#173A5E] font-medium">{photo.author}</span>
      {" · "}
      Licentie:{" "}
      {photo.licenseUrl ? (
        <a
          href={photo.licenseUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[#2E9CD6] hover:text-[#173A5E] underline decoration-dotted"
        >
          {photo.license}
        </a>
      ) : (
        <span className="text-[#173A5E]">{photo.license}</span>
      )}
      {photo.licenseNote && (
        <span className="text-[#5A7594]"> — {photo.licenseNote}</span>
      )}
      {" · "}
      <a
        href={photo.source}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[#2E9CD6] hover:text-[#173A5E]"
      >
        Wikimedia Commons
        <ExternalLink className="w-3 h-3" />
      </a>
    </p>
  </li>
);

export const ColofonPage = () => {
  return (
    <div data-testid="colofon-page" className="bg-white">
      {/* Header */}
      <section className="ll-section-soft py-12 sm:py-16">
        <div className="ll-container max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E4EEF5] text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-wider mb-4">
            <Camera className="w-3.5 h-3.5" />
            Colofon
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Colofon &amp; fotoverantwoording
          </h1>
          <div className="flex items-center gap-3 mb-5" aria-hidden="true">
            <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
            <svg
              width="60"
              height="10"
              viewBox="0 0 60 10"
              className="text-[#6BC0E8]"
            >
              <path
                d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
            De foto's op deze website komen van het lokale nieuwsplatform{" "}
            <a
              href="https://www.lemsternijs.nl"
              target="_blank"
              rel="noreferrer"
              className="ll-link underline decoration-dotted"
            >
              Lemsternijs.nl
            </a>
            , van fotograaf <strong className="text-[#173A5E]">Jan van der Werf</strong>, en van{" "}
            <a
              href="https://commons.wikimedia.org/"
              target="_blank"
              rel="noreferrer"
              className="ll-link underline decoration-dotted"
            >
              Wikimedia Commons
            </a>{" "}
            (vrije licentie). Allemaal met dank, en netjes vermeld.
          </p>
          <p className="mt-3 text-[#3E5871] font-[Inter] text-base leading-relaxed">
            Heb je zelf een mooie foto van Lemmer die we mogen gebruiken? Laat
            het ons weten via{" "}
            <Link to="/voor-ondernemers" className="ll-link">
              Voor ondernemers
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Lemsternijs + Jan van der Werf */}
      <section className="py-10 sm:py-14">
        <div className="ll-container max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">
            Foto's van Lemmer
          </h2>
          <p className="text-sm text-[#5A7594] font-[Inter] mb-6">
            Van Jan van der Werf en Lemsternijs.nl.
          </p>
          <div className="bg-white rounded-2xl border border-[#E4EEF5] shadow-sm p-6 sm:p-8">
            <ul
              data-testid="colofon-photos-lemmer"
              className="divide-y divide-[#F0F5F9]"
            >
              {LEMMER_PHOTOS.map((p) => (
                <LemmerItem key={p.title} photo={p} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Commons */}
      <section className="py-8 sm:py-10">
        <div className="ll-container max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">
            Wikimedia Commons
          </h2>
          <p className="text-sm text-[#5A7594] font-[Inter] mb-6">
            Vrije licenties. We vermelden de makers uit erkenning, ook waar dat
            niet strikt verplicht is.
          </p>
          <div className="bg-white rounded-2xl border border-[#E4EEF5] shadow-sm p-6 sm:p-8">
            <ul
              data-testid="colofon-photos-commons"
              className="divide-y divide-[#F0F5F9]"
            >
              {COMMONS_PHOTOS.map((p) => (
                <CommonsItem key={p.title} photo={p} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <section className="pb-16 pt-6">
        <div className="ll-container max-w-3xl">
          <div className="rounded-2xl bg-[#F4F8FB] border border-[#E4EEF5] p-6 sm:p-8">
            <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-2">
              Over deze website
            </h3>
            <p className="text-[#3E5871] font-[Inter] leading-relaxed mb-2">
              <strong className="text-[#173A5E]">Lemmer Leeft</strong> is een
              initiatief van{" "}
              <span className="text-[#173A5E] font-semibold">BIZ Vereniging Lemmer</span>.
              Ontwikkeling: Ghio Webservices.
            </p>
            <p className="text-[#3E5871] font-[Inter] leading-relaxed">
              Met dank aan{" "}
              <strong className="text-[#173A5E]">Jan van der Werf</strong> en{" "}
              <a
                href="https://www.lemsternijs.nl"
                target="_blank"
                rel="noreferrer"
                className="ll-link"
              >
                Lemsternijs.nl
              </a>{" "}
              voor de foto's van Lemmer.
            </p>
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="ll-btn-outline">
              <ArrowLeft className="w-4 h-4" />
              Terug naar de homepage
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ColofonPage;
